import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useMotionValue, useReducedMotion, useMotionValueEvent } from 'framer-motion';
import Section from './ui/Section';
import GlassCard from './ui/GlassCard';
import StatCard from './ui/StatCard';
import ProcessFramesSection from './ProcessFramesSection';
import ServiceCard from './ui/ServiceCard';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { fadeInUp, revealUp, staggerContainer, staggerItem, ease } from '../lib/animations';
import { useI18n } from '../i18n';
import { trackInstagramClick, trackFaqOpen, trackSectionView } from '../lib/analytics';
import { useInView } from 'react-intersection-observer';

const viewportOnce = { once: true, amount: 0.2 } as const;

// ─── Timeline dust particles ────────────────────────────────────────────────
const TIMELINE_DUST: [number, number, number, number][] = [
  [18, 8, 1.5, 0], [72, 15, 1, 2.4], [35, 28, 1, 4.1], [88, 42, 1.5, 1.2],
  [10, 55, 1, 3.7], [55, 68, 1.5, 0.8], [28, 80, 1, 5.3], [78, 88, 1, 2.0],
  [48, 4, 1, 3.3], [92, 72, 1.5, 1.6],
];

const TIMELINE_STAGES = [
  {
    dayKey: 'timeline.day1',
    titleKey: 'timeline.1.title',
    descKey: 'timeline.1.desc',
    color: '#3B82F6',
    glow: 'rgba(59,130,246,0.7)',
  },
  {
    dayKey: 'timeline.day2',
    titleKey: 'timeline.2.title',
    descKey: 'timeline.2.desc',
    color: '#60A5FA',
    glow: 'rgba(96,165,250,0.6)',
  },
  {
    dayKey: 'timeline.day3',
    titleKey: 'timeline.3.title',
    descKey: 'timeline.3.desc',
    color: '#818CF8',
    glow: 'rgba(129,140,248,0.6)',
  },
];

const SERVICES = [
  {
    num: '01', titleKey: 'services.1.title',
    descKey: 'services.1.desc',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.2"/><path d="M10 10L14 14" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  },
  {
    num: '02', titleKey: 'services.2.title',
    descKey: 'services.2.desc',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 12L6 7L9 10L13 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  },
  {
    num: '03', titleKey: 'services.3.title',
    descKey: 'services.3.desc',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><rect x="2" y="2" width="12" height="12" rx="3" stroke="currentColor" strokeWidth="1.2"/></svg>,
  },
  {
    num: '04', titleKey: 'services.4.title',
    descKey: 'services.4.desc',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8H14M8 2V14" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.2"/></svg>,
  },
  {
    num: '05', titleKey: 'services.5.title',
    descKey: 'services.5.desc',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 13L5 9L8 11L11 6L14 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M11 3H14V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  },
];

const FAQ_ITEMS = [
  {
    qKey: 'faq.1.q',
    aKey: 'faq.1.a',
  },
  {
    qKey: 'faq.2.q',
    aKey: 'faq.2.a',
  },
  {
    qKey: 'faq.3.q',
    aKey: 'faq.3.a',
  },
  {
    qKey: 'faq.4.q',
    aKey: 'faq.4.a',
  },
  {
    qKey: 'faq.5.q',
    aKey: 'faq.5.a',
  },
];

const FAQ_STARS: [number, number, number, number, number][] = [
  [7, 12, 1.4, 0.2, 0.14],
  [18, 28, 1, 2.1, 0.1],
  [31, 9, 1.6, 3.7, 0.12],
  [46, 22, 1.1, 1.3, 0.11],
  [58, 14, 1.5, 4.4, 0.13],
  [73, 18, 1, 0.9, 0.1],
  [84, 8, 1.8, 5.1, 0.12],
  [91, 26, 1.2, 2.7, 0.11],
  [11, 64, 1.1, 1.8, 0.1],
  [24, 78, 1.5, 3.4, 0.13],
  [39, 69, 1, 4.8, 0.09],
  [56, 82, 1.7, 0.4, 0.14],
  [68, 72, 1.1, 2.5, 0.1],
  [81, 84, 1.6, 5.8, 0.13],
  [93, 67, 1, 1.1, 0.09],
];

const FAQ_CRYSTAL_SPARKLES: [string, string, number, number, string][] = [
  ['12%', '20%', 2.5, 0.1, 'rgba(191,219,254,0.85)'],
  ['28%', '74%', 2, 1.5, 'rgba(147,197,253,0.75)'],
  ['40%', '12%', 1.5, 2.7, 'rgba(255,255,255,0.82)'],
  ['52%', '82%', 2.5, 0.8, 'rgba(165,180,252,0.72)'],
  ['66%', '36%', 2, 3.1, 'rgba(147,197,253,0.7)'],
  ['78%', '68%', 1.5, 2.2, 'rgba(255,255,255,0.76)'],
  ['84%', '22%', 2, 4.3, 'rgba(191,219,254,0.72)'],
];

// Tiny particles that drift along the orbital ring
const ORBIT_PARTICLES = [0, 60, 120, 180, 240, 300];

function RequirementsSection() {
  const { t } = useI18n();
  const sectionRef = useRef<HTMLElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 35, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 35, damping: 20 });
  const orbitX = useTransform(springX, [-1, 1], [-10, 10]);
  const orbitY = useTransform(springY, [-1, 1], [-8, 8]);

  const CLIENT_ITEMS = ['requirements.client.1', 'requirements.client.2', 'requirements.client.3', 'requirements.client.4'];
  const MY_ITEMS = ['requirements.my.1', 'requirements.my.2', 'requirements.my.3', 'requirements.my.4', 'requirements.my.5'];

  return (
    <section
      id="requirements"
      ref={sectionRef}
      onMouseMove={(e) => {
        const rect = sectionRef.current?.getBoundingClientRect();
        if (!rect) return;
        mouseX.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
        mouseY.set(((e.clientY - rect.top) / rect.height) * 2 - 1);
      }}
      onMouseLeave={() => { mouseX.set(0); mouseY.set(0); }}
      className="w-full bg-black py-10 md:py-28 px-6 md:px-16 lg:px-24 relative section-glow-top overflow-hidden"
    >
      {/* Ambient glow — centered behind orbit */}
      <div className="absolute pointer-events-none" style={{
        top: '5%', left: '25%', right: '5%', height: '90%',
        background: 'radial-gradient(ellipse at 55% 55%, rgba(37,99,235,0.09) 0%, rgba(99,102,241,0.05) 40%, transparent 68%)',
        filter: 'blur(72px)',
      }} />

      {/* ─── Header — top-left ─────────────────────────────────────────────── */}
      <motion.div
        className="mb-8 md:mb-18 max-w-lg"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={staggerContainer(0.1, 0)}
      >
        <motion.div
          variants={staggerItem}
          className="mb-4 text-[10px] font-medium tracking-[0.28em] uppercase text-white/22"
        >
          {t('requirements.badge')}
        </motion.div>
        <motion.h2
          variants={revealUp}
          className="text-[2rem] md:text-5xl lg:text-[3.5rem] font-extrabold tracking-tight text-white mb-3 md:mb-4 leading-[1.05]"
          style={{ letterSpacing: '-0.03em' }}
        >
          {t('requirements.title1')}<br className="hidden md:block" /> {t('requirements.title2')}
        </motion.h2>
        <motion.p variants={staggerItem} className="text-white/32 text-sm md:text-base leading-relaxed">
          {t('requirements.desc')}
        </motion.p>
      </motion.div>

      {/* ─── Desktop diagram ────────────────────────────────────────────────── */}
      <div className="hidden md:flex items-center justify-between gap-0 relative">

        {/* Left cluster — ОТ ВАС */}
        <motion.div
          className="w-40 lg:w-48 flex-shrink-0 z-10 flex flex-col justify-center"
          initial={{ opacity: 0, x: -48 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.95, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className="flex items-center gap-2 mb-6">
            <motion.div
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: '#3B82F6' }}
              animate={{ boxShadow: ['0 0 3px 1px rgba(59,130,246,0.15)', '0 0 7px 2px rgba(59,130,246,0.55)', '0 0 3px 1px rgba(59,130,246,0.15)'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-blue-400/55">
              {t('requirements.fromYou')}
            </span>
          </div>
          <ul className="space-y-4">
            {CLIENT_ITEMS.map((item, i) => (
              <motion.li
                key={item}
                className="flex items-center gap-2.5"
                initial={{ opacity: 0, x: -22 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.55, delay: 0.35 + i * 0.09, ease: 'easeOut' }}
              >
                <span
                  className="w-[5px] h-[5px] rounded-full flex-shrink-0"
                  style={{ background: 'rgba(59,130,246,0.45)' }}
                />
                <span className="text-white/60 text-base md:text-lg font-semibold leading-snug">
                  {t(item)}
                </span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Connection line — left cluster → orbit */}
        <div className="flex-1 relative h-px mx-6 lg:mx-10 min-w-[40px]">
          <motion.div
            className="absolute inset-0"
            initial={{ scaleX: 0, opacity: 0 }}
            whileInView={{ scaleX: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.3, delay: 0.55, ease: 'easeOut' }}
            style={{
              background: 'linear-gradient(90deg, rgba(59,130,246,0.0) 0%, rgba(59,130,246,0.3) 60%, rgba(59,130,246,0.08) 100%)',
              transformOrigin: 'left center',
            }}
          />
          {/* Travelling dot */}
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full"
            style={{
              background: 'rgba(96,165,250,0.9)',
              filter: 'drop-shadow(0 0 5px rgba(59,130,246,0.9))',
              left: '0%',
            }}
            animate={{ left: ['0%', '95%', '0%'], opacity: [0, 1, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
          />
        </div>

        {/* ─── Orbit — center hero ─────────────────────────── */}
        <motion.div
          className="flex-shrink-0 relative"
          style={{ width: 440, height: 440, x: orbitX, y: orbitY }}
          initial={{ opacity: 0, scale: 0.82 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* Deep ambient glow */}
          <motion.div
            className="absolute inset-[-22%]"
            style={{
              background: 'radial-gradient(ellipse at 50% 50%, rgba(59,130,246,0.13) 0%, rgba(99,102,241,0.07) 42%, transparent 68%)',
              filter: 'blur(52px)',
            }}
            animate={{ opacity: [0.45, 1, 0.45], scale: [1, 1.08, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Sculpture */}
          <motion.img
            src="/installations/orbit-system.png"
            alt={t('requirements.imgAlt1')}
            className="w-full h-full object-contain relative z-10"
            style={{
              filter:
                'drop-shadow(0 0 32px rgba(59,130,246,0.25)) ' +
                'drop-shadow(0 0 88px rgba(99,102,241,0.12))',
            }}
            animate={{ y: [0, -14, 0], rotate: [0, 1.2, 0, -1.2, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Outer orbital ring */}
          <motion.svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 440 440"
            fill="none"
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
            style={{ zIndex: 20 }}
          >
            <ellipse cx="220" cy="220" rx="205" ry="76" stroke="rgba(59,130,246,0.12)" strokeWidth="1" transform="rotate(-20 220 220)" />
            <ellipse cx="220" cy="220" rx="142" ry="53" stroke="rgba(129,140,248,0.09)" strokeWidth="1" transform="rotate(15 220 220)" />
          </motion.svg>

          {/* Outer ring particles */}
          <motion.svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 440 440"
            fill="none"
            animate={{ rotate: 360 }}
            transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
            style={{ zIndex: 21 }}
          >
            {ORBIT_PARTICLES.map((angle, i) => {
              const rad = (angle * Math.PI) / 180;
              const rx = 205, ry = 76, tilt = -20 * (Math.PI / 180);
              const x0 = rx * Math.cos(rad);
              const y0 = ry * Math.sin(rad);
              const x = 220 + x0 * Math.cos(tilt) - y0 * Math.sin(tilt);
              const y = 220 + x0 * Math.sin(tilt) + y0 * Math.cos(tilt);
              const isBlue = i % 2 === 0;
              return (
                <motion.circle
                  key={i}
                  cx={x} cy={y} r={i === 0 ? 2.5 : 1.5}
                  fill={isBlue ? 'rgba(96,165,250,0.9)' : 'rgba(129,140,248,0.75)'}
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
                  style={{ filter: `drop-shadow(0 0 ${isBlue ? 4 : 3}px ${isBlue ? 'rgba(96,165,250,0.8)' : 'rgba(129,140,248,0.7)'})` }}
                />
              );
            })}
          </motion.svg>

          {/* Counter-rotating inner particles */}
          <motion.svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 440 440"
            fill="none"
            animate={{ rotate: -360 }}
            transition={{ duration: 26, repeat: Infinity, ease: 'linear' }}
            style={{ zIndex: 21 }}
          >
            {[0, 90, 180, 270].map((angle, i) => {
              const rad = (angle * Math.PI) / 180;
              const rx = 142, ry = 53, tilt = 15 * (Math.PI / 180);
              const x0 = rx * Math.cos(rad);
              const y0 = ry * Math.sin(rad);
              const x = 220 + x0 * Math.cos(tilt) - y0 * Math.sin(tilt);
              const y = 220 + x0 * Math.sin(tilt) + y0 * Math.cos(tilt);
              return (
                <motion.circle
                  key={i}
                  cx={x} cy={y} r={1.5}
                  fill="rgba(129,140,248,0.6)"
                  animate={{ opacity: [0.2, 0.8, 0.2] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: i * 0.7 }}
                  style={{ filter: 'drop-shadow(0 0 3px rgba(129,140,248,0.7))' }}
                />
              );
            })}
          </motion.svg>
        </motion.div>

        {/* Connection line — orbit → right cluster */}
        <div className="flex-1 relative h-px mx-6 lg:mx-10 min-w-[40px]">
          <motion.div
            className="absolute inset-0"
            initial={{ scaleX: 0, opacity: 0 }}
            whileInView={{ scaleX: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.3, delay: 0.65, ease: 'easeOut' }}
            style={{
              background: 'linear-gradient(90deg, rgba(129,140,248,0.08) 0%, rgba(129,140,248,0.3) 40%, rgba(129,140,248,0.0) 100%)',
              transformOrigin: 'right center',
            }}
          />
          {/* Travelling dot */}
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full"
            style={{
              background: 'rgba(165,180,252,0.9)',
              filter: 'drop-shadow(0 0 5px rgba(129,140,248,0.9))',
              left: '100%',
            }}
            animate={{ left: ['100%', '5%', '100%'], opacity: [0, 1, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 2.0 }}
          />
        </div>

        {/* Right cluster — Я БЕРУ НА СЕБЯ */}
        <motion.div
          className="w-40 lg:w-48 flex-shrink-0 z-10 flex flex-col justify-center"
          initial={{ opacity: 0, x: 48 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.95, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className="flex items-center gap-2 mb-6">
            <motion.div
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: '#818CF8' }}
              animate={{ boxShadow: ['0 0 3px 1px rgba(129,140,248,0.15)', '0 0 7px 2px rgba(129,140,248,0.55)', '0 0 3px 1px rgba(129,140,248,0.15)'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            />
            <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-indigo-300/55">
              {t('requirements.iTake')}
            </span>
          </div>
          <ul className="space-y-4">
            {MY_ITEMS.map((item, i) => (
              <motion.li
                key={item}
                className="flex items-center gap-2.5"
                initial={{ opacity: 0, x: 22 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.55, delay: 0.35 + i * 0.09, ease: 'easeOut' }}
              >
                <span
                  className="w-[5px] h-[5px] rounded-full flex-shrink-0"
                  style={{ background: 'rgba(129,140,248,0.5)' }}
                />
                <span className="text-white/70 text-base md:text-lg font-semibold leading-snug">
                  {t(item)}
                </span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* ─── Mobile fallback — stacked ──────────────────────────────────────── */}
      <div className="md:hidden">
        {/* Orbit — top, centred, closer to header */}
        <div className="flex justify-center mb-5">
          <div className="relative w-52 h-52">
            <motion.div
              className="absolute inset-[-15%]"
              style={{
                background: 'radial-gradient(ellipse at 50% 50%, rgba(59,130,246,0.14) 0%, transparent 65%)',
                filter: 'blur(28px)',
              }}
              animate={{ opacity: [0.4, 0.9, 0.4] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.img
              src="/installations/orbit-system.png"
              alt={t('requirements.imgAlt2')}
              className="w-full h-full object-contain relative z-10"
              style={{ filter: 'drop-shadow(0 0 22px rgba(59,130,246,0.25))' }}
              animate={{ y: [0, -7, 0] }}
              transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
        </div>

        {/* Two columns side by side */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-0">
          {/* Left cluster */}
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-3 text-[10px] font-semibold tracking-[0.3em] uppercase text-blue-400/55">{t('requirements.fromYou')}</div>
            <ul className="space-y-3">
              {CLIENT_ITEMS.map(item => (
                <li key={item} className="flex items-center gap-2">
                  <span className="w-[4px] h-[4px] rounded-full flex-shrink-0" style={{ background: 'rgba(59,130,246,0.45)' }} />
                  <span className="text-white/60 text-sm font-semibold">{t(item)}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right cluster */}
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.07 }}
          >
            <div className="mb-3 text-[10px] font-semibold tracking-[0.3em] uppercase text-indigo-300/55">{t('requirements.mobileTake')}</div>
            <ul className="space-y-3">
              {MY_ITEMS.map(item => (
                <li key={item} className="flex items-center gap-2">
                  <span className="w-[4px] h-[4px] rounded-full flex-shrink-0" style={{ background: 'rgba(129,140,248,0.5)' }} />
                  <span className="text-white/70 text-sm font-semibold">{t(item)}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Bottom note */}
      <motion.p
        className="mt-8 text-white/18 text-xs tracking-wide max-w-md leading-relaxed"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.8 }}
        transition={{ duration: 0.8 }}
      >
        {t('requirements.note')}
      </motion.p>
    </section>
  );
}

// Particle that repels from mouse cursor
function RepelParticle({ baseX, baseY, radius, delay, duration, colorIdx, mousePx, mousePy, sectionW, sectionH }: {
  baseX: number; baseY: number; radius: number; delay: number; duration: number; colorIdx: number;
  mousePx: ReturnType<typeof useMotionValue<number>>; mousePy: ReturnType<typeof useMotionValue<number>>;
  sectionW: number; sectionH: number;
}) {
  // x/y are pixel offsets from the CSS left/top base position
  const dx_ = useMotionValue(0);
  const dy_ = useMotionValue(0);
  const sx = useSpring(dx_, { stiffness: 80, damping: 20 });
  const sy = useSpring(dy_, { stiffness: 80, damping: 20 });

  useMotionValueEvent(mousePx, 'change', (mx: number) => {
    const my = mousePy.get() as number;
    // convert base % to pixels for distance calc
    const bxPx = (baseX / 100) * sectionW;
    const byPx = (baseY / 100) * sectionH;
    const mxPx = (mx / 100) * sectionW;
    const myPx = (my / 100) * sectionH;
    const ddx = bxPx - mxPx;
    const ddy = byPx - myPx;
    const dist = Math.sqrt(ddx * ddx + ddy * ddy);
    const repelRadius = Math.min(sectionW, sectionH) * 0.18;
    if (dist < repelRadius && dist > 0) {
      const force = (1 - dist / repelRadius) * 55;
      dx_.set((ddx / dist) * force);
      dy_.set((ddy / dist) * force);
    } else {
      dx_.set(0);
      dy_.set(0);
    }
  });

  const bg = colorIdx % 3 === 0
    ? 'rgba(147,197,253,0.95)'
    : colorIdx % 3 === 1
    ? 'rgba(199,210,254,0.8)'
    : 'rgba(255,255,255,0.7)';
  const glow = colorIdx % 3 === 0
    ? `0 0 ${radius * 5}px ${radius * 2}px rgba(59,130,246,0.5)`
    : `0 0 ${radius * 5}px ${radius * 2}px rgba(99,102,241,0.4)`;

  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        left: `${baseX}%`, top: `${baseY}%`,
        x: sx, y: sy,
        width: radius * 2, height: radius * 2,
        background: bg,
        boxShadow: glow,
        translateX: '-50%',
        translateY: '-50%',
      }}
      animate={{ opacity: [0.3, 0.85, 0.3], scale: [1, 1.25, 1] }}
      transition={{ duration, delay, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

const SERVICES_PARTICLES: { x: number; y: number; r: number; d: number; dur: number }[] = [
  // Right side (x >= 40%)
  { x: 45, y: 40, r: 1.5, d: 0.9, dur: 10 },
  { x: 52, y: 15, r: 1,   d: 3.5, dur: 8  },
  { x: 48, y: 85, r: 2,   d: 1.1, dur: 12 },
  { x: 58, y: 55, r: 1,   d: 0.5, dur: 9  },
  { x: 62, y: 28, r: 1.5, d: 2.0, dur: 11 },
  { x: 70, y: 10, r: 1,   d: 1.3, dur: 7  },
  { x: 68, y: 65, r: 1.5, d: 3.8, dur: 13 },
  { x: 75, y: 80, r: 1,   d: 0.6, dur: 10 },
  { x: 82, y: 38, r: 2,   d: 1.7, dur: 9  },
  { x: 78, y: 22, r: 1,   d: 4.0, dur: 14 },
  { x: 88, y: 50, r: 1.5, d: 0.2, dur: 8  },
  { x: 85, y: 72, r: 1,   d: 2.5, dur: 11 },
  { x: 93, y: 18, r: 1.5, d: 1.0, dur: 10 },
  { x: 96, y: 88, r: 1,   d: 3.2, dur: 7  },
  { x: 91, y: 42, r: 2,   d: 0.8, dur: 12 },
  { x: 55, y: 95, r: 1,   d: 2.9, dur: 9  },
  { x: 40, y:  5, r: 1.5, d: 1.5, dur: 13 },
  { x: 97, y: 60, r: 1.5, d: 0.4, dur: 11 },
  { x: 63, y: 92, r: 1.5, d: 1.8, dur: 7  },
  { x: 42, y: 55, r: 1,   d: 2.8, dur: 9  },
  { x: 50, y: 70, r: 1.5, d: 1.3, dur: 11 },
  { x: 73, y: 48, r: 1,   d: 3.0, dur: 8  },
  { x: 60, y:  8, r: 1.5, d: 0.7, dur: 13 },
  { x: 94, y: 32, r: 1,   d: 2.2, dur: 10 },
  { x: 47, y: 22, r: 1,   d: 4.1, dur: 9  },
  // Left side (x < 40%) — ~1.5× more density
  { x:  5, y: 12, r: 1,   d: 0,   dur: 9  },
  { x: 14, y: 35, r: 1.5, d: 1.4, dur: 12 },
  { x: 22, y: 58, r: 1,   d: 2.8, dur: 8  },
  { x:  8, y: 78, r: 1.5, d: 0.7, dur: 11 },
  { x: 30, y: 20, r: 1,   d: 3.1, dur: 10 },
  { x: 38, y: 72, r: 1,   d: 1.9, dur: 13 },
  { x: 28, y: 90, r: 1.5, d: 0.3, dur: 7  },
  { x: 18, y:  5, r: 1,   d: 2.2, dur: 14 },
  { x:  3, y: 50, r: 1,   d: 3.3, dur: 8  },
  { x: 33, y: 45, r: 1,   d: 2.1, dur: 10 },
  { x: 12, y: 62, r: 1,   d: 4.2, dur: 14 },
  { x: 10, y: 22, r: 1,   d: 1.0, dur: 10 },
  { x: 25, y: 42, r: 1.5, d: 2.5, dur: 9  },
  { x: 35, y: 65, r: 1,   d: 0.9, dur: 11 },
  { x: 20, y: 85, r: 1.5, d: 3.0, dur: 8  },
  { x:  6, y: 48, r: 1,   d: 1.7, dur: 13 },
  { x: 16, y: 70, r: 1,   d: 4.5, dur: 10 },
  { x: 32, y: 10, r: 1.5, d: 0.5, dur: 12 },
  { x: 24, y: 30, r: 1,   d: 1.2, dur: 11 },
  { x: 38, y: 88, r: 1.5, d: 3.7, dur: 7  },
  { x:  2, y: 28, r: 1,   d: 2.6, dur: 9  },
  { x: 15, y: 50, r: 1.5, d: 0.8, dur: 12 },
  { x: 27, y: 75, r: 1,   d: 3.4, dur: 8  },
  { x:  9, y: 95, r: 1,   d: 1.5, dur: 11 },
  { x: 36, y: 33, r: 1.5, d: 0.2, dur: 10 },
  { x: 21, y: 15, r: 1,   d: 4.8, dur: 13 },
  { x:  4, y: 68, r: 1.5, d: 1.9, dur: 7  },
  { x: 29, y: 52, r: 1,   d: 3.1, dur: 9  },
  { x: 13, y: 82, r: 1,   d: 0.6, dur: 14 },
  { x: 39, y:  8, r: 1.5, d: 2.3, dur: 10 },
  { x:  7, y: 38, r: 1,   d: 1.1, dur: 8  },
  { x: 23, y: 97, r: 1.5, d: 3.9, dur: 11 },
];

function ServicesSection() {
  const { t } = useI18n();
  const sectionRef = useRef<HTMLElement>(null);
  const [sectionSize, setSectionSize] = React.useState({ w: 1440, h: 600 });
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  // pixel-space mouse for repel
  const mousePx = useMotionValue<number>(-9999);
  const mousePy = useMotionValue<number>(-9999);

  const springX = useSpring(mouseX, { stiffness: 40, damping: 18 });
  const springY = useSpring(mouseY, { stiffness: 40, damping: 18 });
  const parallaxX = useTransform(springX, [-1, 1], [-14, 14]);
  const parallaxY = useTransform(springY, [-1, 1], [-10, 10]);

  React.useEffect(() => {
    const update = () => {
      if (sectionRef.current) {
        const r = sectionRef.current.getBoundingClientRect();
        setSectionSize({ w: r.width, h: r.height });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rx = e.clientX - rect.left;
    const ry = e.clientY - rect.top;
    mouseX.set((rx / rect.width) * 2 - 1);
    mouseY.set((ry / rect.height) * 2 - 1);
    mousePx.set((rx / rect.width) * 100);
    mousePy.set((ry / rect.height) * 100);
  };

  return (
    <section
      id="services"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => { mouseX.set(0); mouseY.set(0); mousePx.set(-9999); mousePy.set(-9999); }}
      className="w-full bg-black py-10 md:py-28 px-6 md:px-16 lg:px-24 relative section-glow-top overflow-hidden"
    >
      {/* Ambient light */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          top: '-10%', right: '-5%', width: '55%', height: '120%',
          background: 'radial-gradient(ellipse at 70% 50%, rgba(59,130,246,0.07) 0%, rgba(99,102,241,0.04) 35%, transparent 65%)',
          filter: 'blur(60px)',
        }}
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Full-section particle field with repel */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden" style={{ zIndex: 0 }}>
        {SERVICES_PARTICLES.map((p, i) => (
          <RepelParticle
            key={i}
            baseX={p.x}
            baseY={p.y}
            radius={p.r}
            delay={p.d}
            duration={p.dur}
            colorIdx={i}
            mousePx={mousePx}
            mousePy={mousePy}
            sectionW={sectionSize.w}
            sectionH={sectionSize.h}
          />
        ))}
      </div>

      <div className="relative flex flex-col md:flex-row md:gap-20 lg:gap-32">

        {/* Left — headline column */}
        <motion.div
          className="md:w-[38%] mb-8 md:mb-0 flex flex-col justify-between"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer(0.1, 0)}
        >
          <div>
            <motion.div variants={staggerItem} className="mb-5 text-[11px] font-medium tracking-[0.22em] uppercase text-white/25">
              {t('services.badge')}
            </motion.div>
            <motion.h2
              variants={revealUp}
              className="text-[2rem] md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight text-white mb-4 md:mb-6"
              style={{ letterSpacing: '-0.02em' }}
            >
              {t('services.title')}
            </motion.h2>
            <motion.p variants={staggerItem} className="text-white/35 text-base md:text-lg leading-relaxed max-w-xs">
              {t('services.desc')}
            </motion.p>
          </div>
          <div className="hidden md:block mt-12">
            <div className="w-12 h-px" style={{ background: 'linear-gradient(90deg, #3B82F6, transparent)', opacity: 0.4 }} />
          </div>
        </motion.div>

        {/* Right — service rows + installation */}
        <div className="md:flex-1 relative">

          {/* Service rows */}
          <div className="relative space-y-0 divide-y divide-white/[0.06]" style={{ zIndex: 1 }}>
            {SERVICES.map((item, i) => (
              <motion.div
                key={item.num}
                className="group flex items-start gap-5 py-6 cursor-default relative"
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.07, ease }}
                whileHover={{ x: 3 }}
              >
                {/* Hover highlight line */}
                <motion.div
                  className="absolute left-0 top-0 bottom-0 w-px origin-top"
                  style={{ background: 'linear-gradient(180deg, transparent, #3B82F6, transparent)' }}
                  initial={{ scaleY: 0, opacity: 0 }}
                  whileHover={{ scaleY: 1, opacity: 1 }}
                  transition={{ duration: 0.35 }}
                />

                {/* Number */}
                <span className="text-[11px] font-medium text-white/20 group-hover:text-white/40 tracking-widest pt-[2px] w-6 flex-shrink-0 transition-colors duration-300">
                  {item.num}
                </span>

                {/* Icon */}
                <motion.span
                  className="flex-shrink-0 mt-[1px] transition-all duration-300"
                  style={{ color: 'rgba(59,130,246,0.45)' }}
                  whileHover={{ color: 'rgba(96,165,250,0.9)', filter: 'drop-shadow(0 0 6px rgba(59,130,246,0.7))' } as any}
                >
                  {item.icon}
                </motion.span>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-white/80 font-semibold text-base tracking-tight group-hover:text-blue-300 transition-colors duration-250">
                      {t(item.titleKey)}
                    </h3>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 10L10 2M10 2H4M10 2V8" stroke="#3B82F6" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                  <p className="text-white/30 text-sm leading-relaxed mt-1">{t(item.descKey)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineSection() {
  const { t } = useI18n();
  const sectionRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const lineScaleX = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const glowX = useTransform(lineScaleX, (v) => `${Math.max(0, Math.min(100, v * 100))}%`);
  const mobileScaleY = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const [activeIndex, setActiveIndex] = useState(0);

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    if (latest < 0.17) setActiveIndex(0);
    else if (latest < 0.34) setActiveIndex(1);
    else setActiveIndex(2);
  });

  return (
    <section
      id="timeline"
      ref={sectionRef}
      className="relative w-full bg-black section-glow-top"
      style={{ height: '200vh' }}
    >
      <div
        className="absolute pointer-events-none"
        style={{
          top: '30%',
          left: '10%',
          right: '10%',
          height: '55%',
          background: 'radial-gradient(ellipse at 50% 60%, rgba(59,130,246,0.07) 0%, rgba(99,102,241,0.04) 40%, transparent 70%)',
          filter: 'blur(48px)',
        }}
      />

      {TIMELINE_DUST.map(([top, left, s, d], i) => (
        <motion.div
          key={i}
          aria-hidden="true"
          className="absolute rounded-full pointer-events-none"
          style={{
            top: `${top}%`, left: `${left}%`, width: s, height: s,
            background: i % 2 === 0 ? 'rgba(96,165,250,0.7)' : 'rgba(129,140,248,0.6)',
          }}
          animate={prefersReducedMotion ? { opacity: 0.2 } : { opacity: [0, 0.45, 0], y: [0, -10, 0] }}
          transition={{ duration: 5 + (i % 3) * 1.4, repeat: Infinity, ease: 'easeInOut', delay: d }}
        />
      ))}

      <div className="hidden md:flex sticky top-0 h-screen overflow-hidden flex-col justify-center px-6 md:px-16 lg:px-24 py-20 md:py-28">
        <motion.div
          className="max-w-xl mb-14 md:mb-20"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer(0.1, 0)}
        >
          <motion.div variants={staggerItem} className="mb-5 text-[11px] font-medium tracking-[0.22em] uppercase text-white/25">
            {t('timeline.badge')}
          </motion.div>
          <motion.h2
            variants={revealUp}
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-5 leading-tight"
            style={{ letterSpacing: '-0.025em' }}
          >
            {t('timeline.title')}
          </motion.h2>
          <motion.p variants={staggerItem} className="text-white/40 text-base md:text-lg leading-relaxed">
            {t('timeline.desc')}
          </motion.p>
        </motion.div>

        <div className="relative">
          <div className="absolute top-[34px] left-0 right-0 h-px bg-white/[0.06]" />

          <motion.div
            className="absolute top-[34px] left-0 right-0 h-px origin-left"
            style={{
              scaleX: lineScaleX,
              background: 'linear-gradient(90deg, #1D4ED8 0%, #3B82F6 45%, #818CF8 100%)',
              boxShadow: '0 0 8px rgba(59,130,246,0.35), 0 0 18px rgba(59,130,246,0.14)',
            }}
          />

          <motion.div
            className="absolute top-[28px] pointer-events-none"
            style={{ left: glowX, translateX: '-50%' }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: '#93C5FD',
                boxShadow: '0 0 10px 3px rgba(147,197,253,0.35), 0 0 18px 6px rgba(59,130,246,0.16)',
              }}
            />
          </motion.div>

          <div className="grid grid-cols-3 gap-12 pt-0">
            {TIMELINE_STAGES.map((stage, i) => {
              const isActive = i === activeIndex;
              const isPast = i < activeIndex;
              const titleColor = isActive ? 'rgba(255,255,255,0.96)' : isPast ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.12)';
              const textColor = isActive ? 'rgba(255,255,255,0.46)' : isPast ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.05)';

              return (
                <motion.div
                  key={stage.dayKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.65, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="relative pt-0 cursor-default"
                  animate={{ opacity: isActive ? 1 : isPast ? 0.82 : 0.42 }}
                >
                  <div className="flex flex-col items-start">
                    <span
                      className="mb-8 text-[11px] font-medium tracking-[0.18em] uppercase transition-colors duration-300"
                      style={{ color: isActive || isPast ? stage.color : 'rgba(255,255,255,0.08)' }}
                    >
                      {t(stage.dayKey)}
                    </span>

                    <motion.div
                      className="absolute top-[28px] left-0 w-[12px] h-[12px] rounded-full"
                      animate={{
                        scale: isActive ? 1.18 : 1,
                        opacity: isActive ? 1 : isPast ? 0.88 : 0.52,
                      }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      style={{
                        background: stage.color,
                        boxShadow: isActive
                          ? `0 0 8px 2px ${stage.glow.replace('0.7', '0.28').replace('0.6', '0.24')}`
                          : isPast
                            ? `0 0 4px 1px ${stage.glow.replace('0.7', '0.16').replace('0.6', '0.16')}`
                            : 'none',
                      }}
                    />
                  </div>

                  <motion.div
                    animate={{ y: isActive ? -2 : 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="pt-14"
                  >
                    <h3
                      className="font-bold text-xl mb-3 tracking-tight transition-colors duration-300"
                      style={{ color: titleColor, letterSpacing: '-0.015em' }}
                    >
                      {t(stage.titleKey)}
                    </h3>
                    <p className="text-sm leading-relaxed transition-colors duration-300" style={{ color: textColor }}>
                      {t(stage.descKey)}
                    </p>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="md:hidden px-6 py-10">
        <motion.div
          className="mb-6 max-w-xl"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer(0.1, 0)}
        >
          <motion.div variants={staggerItem} className="mb-3 text-[11px] font-medium tracking-[0.22em] uppercase text-white/25">
            {t('timeline.badge')}
          </motion.div>
          <motion.h2
            variants={revealUp}
            className="mb-3 text-[2rem] font-extrabold tracking-tight text-white leading-tight"
            style={{ letterSpacing: '-0.025em' }}
          >
            {t('timeline.title')}
          </motion.h2>
          <motion.p variants={staggerItem} className="text-white/40 text-base leading-relaxed">
            {t('timeline.desc')}
          </motion.p>
        </motion.div>

        <div className="relative pl-7">
          <div className="absolute bottom-0 left-[10px] top-0 w-px bg-white/[0.08]" />
          <motion.div
            className="absolute bottom-0 left-[10px] top-0 w-px origin-top"
            style={{
              scaleY: mobileScaleY,
              background: 'linear-gradient(180deg, rgba(37,99,235,0.95) 0%, rgba(59,130,246,0.9) 60%, rgba(129,140,248,0.85) 100%)',
            }}
          />
          <div className="space-y-8">
            {TIMELINE_STAGES.map((stage, i) => (
              <motion.div
                key={stage.dayKey}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="relative"
              >
                <div
                  className="absolute -left-[22px] top-[6px] h-[12px] w-[12px] rounded-full border"
                  style={{
                    background: stage.color,
                    borderColor: `${stage.color}66`,
                    boxShadow: `0 0 0 4px ${stage.color}18`,
                  }}
                />
                <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.2em]" style={{ color: stage.color }}>
                  {t(stage.dayKey)}
                </div>
                <h3 className="mb-1.5 text-white text-lg font-bold tracking-tight">{t(stage.titleKey)}</h3>
                <p className="text-sm leading-relaxed text-white/45">{t(stage.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const MagneticButton = ({ href, label, icon, primary }: any) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: (e.clientX - rect.left - rect.width / 2) * 0.2,
      y: (e.clientY - rect.top - rect.height / 2) * 0.2,
    });
  };

  return (
    <motion.a
      href={href}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setMousePosition({ x: 0, y: 0 });
        setIsHovered(false);
      }}
      animate={{
        x: mousePosition.x,
        y: mousePosition.y,
      }}
      whileHover={{ scale: 1.05 }}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-medium px-8 py-4 transition ${
        primary
          ? 'bg-white text-black hover:bg-white/90'
          : 'border border-white/20 text-white hover:border-white/40'
      }`}
    >
      {label}
      {icon && <span aria-hidden>{icon}</span>}
    </motion.a>
  );
};

const FAQItem = ({ item, index, reduceMotion }: any) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(index === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className={`border-b transition-colors duration-300 ${
        isOpen ? 'border-blue-400/20' : 'border-white/[0.07]'
      }`}
    >
      {/* Subtle blue left accent when open */}
      {isOpen && (
        <div
          className="absolute left-0 top-0 bottom-0 w-px"
          style={{ background: 'linear-gradient(180deg, transparent, rgba(96,165,250,0.35), transparent)' }}
        />
      )}

      <button
        onClick={() => { const next = !isOpen; setIsOpen(next); if (next) trackFaqOpen(item.qKey); }}
        aria-expanded={isOpen}
        className="relative w-full text-left py-5 md:py-6 flex items-center justify-between gap-6"
      >
        <div className="flex items-center gap-3.5 min-w-0">
          <span
            className="w-[7px] h-[7px] rounded-full flex-shrink-0 transition-all duration-400"
            style={{
              background: isOpen ? '#7DD3FC' : 'rgba(59,130,246,0.45)',
              boxShadow: isOpen ? '0 0 8px rgba(125,211,252,0.6)' : 'none',
            }}
          />
          <span
            className={`font-semibold text-base md:text-[17px] tracking-tight transition-colors duration-300 leading-snug ${
              isOpen ? 'text-white' : 'text-white/65 hover:text-white/90'
            }`}
          >
            {t(item.qKey)}
          </span>
        </div>
        <motion.span
          aria-hidden="true"
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
            isOpen
              ? 'border-blue-400/30 bg-blue-400/8 text-blue-200'
              : 'border-white/10 text-white/30 hover:border-white/18 hover:text-white/55'
          }`}
        >
          <span className="absolute h-px w-3.5 rounded-full bg-current" />
          <span className="absolute h-3.5 w-px rounded-full bg-current" />
        </motion.span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, height: 'auto' }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: reduceMotion ? 0.2 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-5 pl-[26px] pr-4 md:pr-12">
              <p className="text-white/50 text-sm md:text-[15px] leading-relaxed">
                {t(item.aKey)}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

function useSectionTracking(section: string) {
  const { ref } = useInView({
    threshold: 0.3,
    triggerOnce: true,
    onChange: (inView) => { if (inView) trackSectionView(section); },
  });
  return ref;
}

export default function AlinaLanding() {
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();

  const budgetRef = useSectionTracking('budget');
  const faqRef = useSectionTracking('faq');
  const contactRef = useSectionTracking('contact');

  return (
    <div className="w-full bg-black text-white">
      {/* Statistics Section */}
      <Section id="stats" showDivider={false} className="py-12 md:py-20">
        <div className="relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-4 md:gap-y-0 md:divide-x divide-white/[0.06]">
            <StatCard index={0} value="2+" label={t('stats.exp')} accentColor="blue" />
            <StatCard index={1} value="50+" label={t('stats.launches')} accentColor="blue" />
            <StatCard index={2} value="3–7" label={t('stats.days')} accentColor="blue" />
            <StatCard index={3} value="100%" label={t('stats.data')} accentColor="purple" />
          </div>
        </div>
      </Section>

      <ProcessFramesSection />

      {/* Section bridge: Process → Timeline */}
      <div className="relative h-px w-full overflow-visible" aria-hidden="true">
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.12) 30%, rgba(96,165,250,0.18) 50%, rgba(59,130,246,0.12) 70%, transparent 100%)' }} />
        <div className="absolute left-1/2 -translate-x-1/2 -top-16 w-[1px] h-16" style={{ background: 'linear-gradient(to bottom, transparent, rgba(59,130,246,0.12))' }} />
      </div>

      <TimelineSection />

      <ServicesSection />

      {/* Section bridge: Services → Requirements */}
      <div className="relative h-px w-full" aria-hidden="true">
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.1) 40%, rgba(59,130,246,0.1) 60%, transparent 100%)' }} />
      </div>

      <RequirementsSection />

      {/* Section bridge: Requirements → Budget */}
      <div className="relative h-px w-full" aria-hidden="true">
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.1) 40%, rgba(59,130,246,0.1) 60%, transparent 100%)' }} />
      </div>

      {/* Budget Section */}
      <section ref={budgetRef} id="budget" className="w-full bg-black py-10 md:py-28 px-6 md:px-16 lg:px-24 relative section-glow-top overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-32 pointer-events-none z-10" style={{ background: 'linear-gradient(to bottom, #000 0%, transparent 100%)' }} />
        <div className="absolute inset-x-0 bottom-0 h-32 pointer-events-none z-10" style={{ background: 'linear-gradient(to top, #000 0%, transparent 100%)' }} />

        {/* Ambient glow — pulls toward the right card */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '20%', right: '-5%', width: '50%', height: '70%',
            background: 'radial-gradient(ellipse at 65% 50%, rgba(37,99,235,0.09) 0%, rgba(99,102,241,0.05) 40%, transparent 70%)',
            filter: 'blur(56px)',
          }}
        />

        {/* Header */}
        <motion.div
          className="mb-8 md:mb-16 max-w-2xl"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer(0.1, 0)}
        >
          <motion.div variants={staggerItem} className="mb-5 text-[11px] font-medium tracking-[0.22em] uppercase text-white/25">
            {t('budget.badge')}
          </motion.div>
          <motion.h2
            variants={revealUp}
            className="text-[2rem] md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-3 md:mb-5 leading-tight"
            style={{ letterSpacing: '-0.02em' }}
          >
            {t('budget.title1')}<br className="hidden md:block" /> {t('budget.title2')}
          </motion.h2>
          <motion.p variants={staggerItem} className="text-white/35 text-base md:text-lg leading-relaxed max-w-sm">
            {t('budget.desc')}
          </motion.p>
        </motion.div>

        {/* Comparison columns */}
        <div className="grid md:grid-cols-2 gap-4 mb-8 md:mb-16">

          {/* Without system — intentionally muted */}
          <motion.div
            className="rounded-2xl px-5 py-6 md:px-10 md:py-12"
            style={{
              background: 'rgba(255,255,255,0.015)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-[7px] h-[7px] rounded-full bg-white/15" />
              <span className="text-[11px] font-medium tracking-[0.2em] uppercase text-white/20">{t('budget.without')}</span>
            </div>
            <ul className="space-y-4">
              {[
                'budget.with.1',
                'budget.with.2',
                'budget.with.3',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-[7px] w-3 h-px bg-white/12 flex-shrink-0 block" />
                  <span className="text-white/20 text-sm leading-snug">{t(item)}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* With system — glass premium card */}
          <motion.div
            className="relative rounded-2xl px-5 py-6 md:px-10 md:py-12 cursor-default"
            style={{
              background: 'rgba(255,255,255,0.032)',
              border: '1px solid rgba(59,130,246,0.22)',
              boxShadow: '0 0 0 0px rgba(59,130,246,0)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.6, delay: 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
            whileHover={{
              y: -4,
              boxShadow: '0 8px 48px rgba(59,130,246,0.14), 0 0 0 1px rgba(59,130,246,0.32)',
            }}
          >
            {/* Breathing glow behind card */}
            <motion.div
              className="absolute inset-[-1px] rounded-2xl pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at 50% 0%, rgba(59,130,246,0.1) 0%, transparent 60%)',
                filter: 'blur(16px)',
              }}
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Top edge glow line */}
            <div
              className="absolute top-0 left-[10%] right-[10%] h-px rounded-full pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(96,165,250,0.5) 40%, rgba(129,140,248,0.4) 60%, transparent)',
              }}
            />

            {/* Inner glass sheen — top-left corner */}
            <div
              className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%)',
              }}
            />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <motion.div
                  className="w-[9px] h-[9px] rounded-full"
                  style={{ background: '#3B82F6' }}
                  animate={{
                    boxShadow: [
                      '0 0 4px 1px rgba(59,130,246,0.3)',
                      '0 0 10px 3px rgba(59,130,246,0.7)',
                      '0 0 4px 1px rgba(59,130,246,0.3)',
                    ],
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <span className="text-sm font-semibold tracking-[0.14em] uppercase text-blue-400">{t('budget.system')}</span>
              </div>

              <ul className="space-y-5">
                {[
                  'budget.system.1',
                  'budget.system.2',
                  'budget.system.3',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-[5px] flex-shrink-0" style={{ color: '#60A5FA', lineHeight: 1 }}>
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7L6 11L12 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <span className="text-white/80 text-base font-medium leading-relaxed">{t(item)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Factors pills */}
        <div>
          <motion.div
            className="mb-5 text-[11px] font-medium tracking-[0.2em] uppercase text-white/20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {t('budget.factors')}
          </motion.div>
          <motion.div
            className="flex flex-wrap gap-2.5"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={staggerContainer(0.07, 0)}
          >
            {[
              'budget.factor.1',
              'budget.factor.2',
              'budget.factor.3',
              'budget.factor.4',
              'budget.factor.5',
            ].map((factor) => (
              <motion.span
                key={factor}
                variants={staggerItem}
                className="text-xs px-5 py-2.5 rounded-full cursor-default tracking-wide transition-all duration-300"
                style={{
                  border: '1px solid rgba(59,130,246,0.16)',
                  color: 'rgba(255,255,255,0.38)',
                  background: 'rgba(59,130,246,0.03)',
                }}
                whileHover={{
                  color: 'rgba(255,255,255,0.72)',
                  borderColor: 'rgba(96,165,250,0.38)',
                  background: 'rgba(59,130,246,0.07)',
                  boxShadow: '0 0 14px rgba(59,130,246,0.12)',
                  y: -1,
                }}
                transition={{ duration: 0.2 }}
              >
                {t(factor)}
              </motion.span>
            ))}
          </motion.div>
        </div>

      </section>

      {/* FAQ Section */}
      <section ref={faqRef} id="faq" className="w-full bg-black py-10 md:py-28 px-6 md:px-16 lg:px-24 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-32 pointer-events-none z-10" style={{ background: 'linear-gradient(to bottom, #000 0%, transparent 100%)' }} />
        <div className="absolute inset-x-0 bottom-0 h-32 pointer-events-none z-10" style={{ background: 'linear-gradient(to top, #000 0%, transparent 100%)' }} />

        {/* Subtle ambient — top-left, contained */}
        <div className="absolute pointer-events-none" style={{
          top: '-10%', left: '-5%', width: '50%', height: '70%',
          background: 'radial-gradient(ellipse at 30% 40%, rgba(37,99,235,0.06) 0%, rgba(99,102,241,0.03) 45%, transparent 70%)',
          filter: 'blur(72px)',
        }} />

        {/* Ambient glow — right side, behind accordion */}
        <div className="absolute pointer-events-none" style={{
          top: '0%', right: '-8%', width: '55%', height: '100%',
          background: 'radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.055) 0%, rgba(99,102,241,0.025) 45%, transparent 70%)',
          filter: 'blur(80px)',
        }} />

        {/* Subtle stars */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {FAQ_STARS.map(([left, top, size, delay, opacity], i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${left}%`, top: `${top}%`,
                width: size, height: size,
                background: 'rgba(191,219,254,0.9)',
              }}
              animate={prefersReducedMotion ? { opacity: opacity * 0.5 } : { opacity: [opacity * 0.3, opacity * 0.7, opacity * 0.3] }}
              transition={{ duration: 4.4 + (i % 4) * 0.8, repeat: Infinity, ease: 'easeInOut', delay }}
            />
          ))}
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:gap-16 lg:gap-24">

          {/* Left: heading */}
          <div className="md:w-[38%] lg:w-[34%] mb-6 md:mb-0 flex-shrink-0">
            <div className="md:sticky md:top-24">
              <motion.div
                className="mb-4 text-[10px] font-medium tracking-[0.28em] uppercase text-white/22"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.6 }}
              >
                {t('faq.badge')}
              </motion.div>
              <motion.h2
                className="text-[2rem] md:text-5xl lg:text-[3.25rem] font-extrabold text-white leading-[1.05] mb-3 md:mb-5"
                style={{ letterSpacing: '-0.03em' }}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.7, delay: 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {t('faq.title1')}<br className="hidden md:block" /> {t('faq.title2')}
              </motion.h2>
              <motion.p
                className="text-white/32 text-sm md:text-base leading-relaxed"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {t('faq.desc')}
              </motion.p>
            </div>
          </div>

          {/* Right: accordion */}
          <div className="flex-1 min-w-0">
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem key={item.qKey} item={item} index={i} reduceMotion={prefersReducedMotion} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        ref={contactRef}
        id="contact"
        className="w-full bg-black py-6 md:py-40 px-6 md:px-16 lg:px-24 relative overflow-hidden"
      >
        <div className="absolute inset-x-0 top-0 h-32 pointer-events-none z-10" style={{ background: 'linear-gradient(to bottom, #000 0%, transparent 100%)' }} />
        <div className="absolute inset-x-0 bottom-0 h-32 pointer-events-none z-10" style={{ background: 'linear-gradient(to top, #000 0%, transparent 100%)' }} />

        {/* ─── Cinematic atmosphere: full-section ─── */}

        {/* Primary nebula — deep blue, centre-left, slow drift */}
        <motion.div aria-hidden="true" className="absolute pointer-events-none"
          style={{ top: '-30%', left: '-15%', width: '80%', height: '160%',
            background: 'radial-gradient(ellipse at 38% 50%, rgba(29,78,216,0.1) 0%, rgba(59,130,246,0.05) 38%, transparent 68%)',
            filter: 'blur(90px)' }}
          animate={{ x: [0, 22, 0], y: [0, -14, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }} />

        {/* Secondary nebula — indigo, top-right */}
        <motion.div aria-hidden="true" className="absolute pointer-events-none"
          style={{ top: '-15%', right: '-20%', width: '65%', height: '130%',
            background: 'radial-gradient(ellipse at 62% 42%, rgba(79,70,229,0.09) 0%, rgba(99,102,241,0.04) 42%, transparent 68%)',
            filter: 'blur(100px)' }}
          animate={{ x: [0, -18, 0], y: [0, 20, 0] }}
          transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut', delay: 4 }} />

        {/* Volumetric fog — centre, very wide and faint */}
        <motion.div aria-hidden="true" className="absolute pointer-events-none"
          style={{ top: '10%', left: '20%', width: '60%', height: '80%',
            background: 'radial-gradient(ellipse at 50% 50%, rgba(59,130,246,0.04) 0%, transparent 60%)',
            filter: 'blur(60px)' }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }} />

        {/* ─── Full-section starfield: denser & brighter toward the right (diamond side) ─── */}
        {([
          // left zone 0–30%: very dim, tiny
          [8,  3, 1,  0,   0.25],[22, 8, 1,  2.3, 0.2 ],[5,  18,1.5,4.1, 0.22],[17, 26,1,  1.1, 0.18],
          [38, 2, 1,  3.4, 0.2 ],[50, 7, 1,  0.7, 0.18],[64, 12,1,  5.2, 0.22],[78, 22,1.5,2.8, 0.2 ],
          [90, 5, 1,  1.6, 0.18],[12, 42,1,  4.7, 0.2 ],[28, 55,1,  0.3, 0.22],[44, 63,1,  3.1, 0.18],
          [70, 48,1,  5.8, 0.2 ],[85, 38,1.5,2.0, 0.22],[95, 58,1,  1.4, 0.18],[6,  72,1,  4.4, 0.2 ],
          [20, 85,1,  0.9, 0.18],[55, 78,1,  3.7, 0.22],[74, 92,1.5,2.5, 0.2 ],[42, 88,1,  5.5, 0.18],
          // mid zone 30–55%: medium
          [9,  32,1.5,1.2, 0.35],[25, 37,1,  3.6, 0.3 ],[46, 34,1.5,0.5, 0.38],[61, 42,1,  2.2, 0.32],
          [80, 30,1.5,4.9, 0.35],[14, 50,1,  1.8, 0.3 ],[33, 46,1.5,5.3, 0.38],[58, 52,1,  0.2, 0.32],
          [77, 44,2,  3.3, 0.4 ],[92, 50,1.5,2.6, 0.35],[3,  36,1,  4.0, 0.3 ],[49, 40,1,  1.5, 0.28],
          // right zone 55–100%: bright, larger — near diamond
          [7,  58,2,  0,   0.7 ],[19, 65,1.5,2.1, 0.6 ],[35, 72,2,  4.5, 0.75],[52, 68,1.5,1.2, 0.65],
          [66, 60,2.5,3.8, 0.8 ],[81, 74,2,  0.6, 0.7 ],[93, 62,1.5,5.3, 0.55],[10, 80,2,  1.9, 0.65],
          [27, 88,2.5,3.1, 0.75],[48, 76,2,  4.8, 0.7 ],[60, 95,1.5,2.7, 0.6 ],[72, 83,2,  0.4, 0.72],
          [86, 90,2.5,6.0, 0.8 ],[40, 57,2,  5.8, 0.65],[15, 93,1.5,1.5, 0.55],[88, 66,2,  3.4, 0.7 ],
          [53, 84,2,  2.9, 0.68],[97, 78,1.5,0.9, 0.6 ],[30, 60,2,  4.1, 0.62],[74, 70,2.5,3.6, 0.78],
          [43, 96,2,  1.7, 0.65],[63, 87,1.5,5.1, 0.6 ],[21, 77,2,  2.4, 0.7 ],[57, 62,2.5,0.8, 0.75],
        ] as [number,number,number,number,number][]).map(([top,left,s,d,maxOp], i) => (
          <motion.div key={i} aria-hidden="true" className="absolute rounded-full pointer-events-none"
            style={{ top: `${top}%`, left: `${left}%`, width: s, height: s,
              background: i % 3 === 0 ? 'rgba(129,140,248,1)' : 'rgba(96,165,250,1)' }}
            animate={{ opacity: [0, maxOp, 0] }}
            transition={{ duration: 2.8 + (i % 5) * 0.7, repeat: Infinity, ease: 'easeInOut', delay: d }} />
        ))}

        {/* ─── Content: Centered, Minimalist, Powerful CTA ─── */}
        <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} viewport={{ once: true }}
            className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full mb-8 border border-white/10 bg-white/5 backdrop-blur-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]" />
            <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-white/60 pt-px">
              {t('cta.badge')}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }} viewport={{ once: true }}
            className="font-extrabold leading-[1.05] mb-8 drop-shadow-[0_2px_24px_rgba(0,0,0,0.75)]"
            style={{ fontSize: 'clamp(2.5rem, 6vw + 1rem, 5rem)', letterSpacing: '-0.03em' }}
          >
            <span className="text-white">{t('cta.title1')}</span><br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400">{t('cta.title2')}</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }} viewport={{ once: true }}
            className="text-white/60 text-lg md:text-xl leading-relaxed mb-12 max-w-2xl mx-auto"
          >
            {t('cta.desc')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.22 }} viewport={{ once: true }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full"
          >
            <div className="relative inline-block">
              {!prefersReducedMotion && (
                <motion.div
                  className="absolute -inset-3 rounded-full pointer-events-none"
                  style={{
                    background: 'radial-gradient(ellipse at 50% 50%, rgba(99,102,241,0.32) 0%, rgba(59,130,246,0.22) 40%, transparent 72%)',
                    filter: 'blur(18px)',
                  }}
                  animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.08, 1] }}
                  transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}
              <motion.a
                href="https://www.instagram.com/mysyshyn_target/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackInstagramClick('final_cta')}
                className="group relative inline-flex items-center gap-3 px-10 py-5 rounded-full font-bold text-base md:text-lg text-white overflow-hidden shadow-[0_4px_32px_rgba(59,130,246,0.35),0_0_0_1px_rgba(255,255,255,0.1)_inset,0_1px_0_rgba(255,255,255,0.15)_inset]"
                style={{
                  background: 'linear-gradient(120deg, #1d4ed8 0%, #3b82f6 45%, #6366f1 78%, #7c3aed 100%)',
                }}
                whileHover={{ scale: 1.03, boxShadow: '0 8px 40px rgba(99,102,241,0.45), 0 0 0 1px rgba(255,255,255,0.14) inset' }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 22 }}
              >
                <span
                  className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none"
                  style={{
                    background: 'linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.25) 45%, rgba(255,255,255,0.05) 55%, transparent 100%)',
                  }}
                />
                <span className="relative z-10 flex items-center gap-2 drop-shadow-md">
                  {t('cta.button')}
                  <svg
                    className="w-5 h-5 ml-1 transform group-hover:translate-x-1 transition-transform"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M3 8h8M9 4l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </motion.a>
            </div>
            
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 px-4 py-3 rounded-full backdrop-blur-sm">
              <div className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </div>
              <span className="text-[10px] md:text-xs tracking-widest uppercase font-semibold text-white/50 pt-px">
                {t('cta.response')}
              </span>
            </div>
          </motion.div>
        </div>

      </section>

      {/* Footer */}
      <footer className="relative w-full py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-white font-bold mb-4">{t('footer.title')}</h3>
              <p className="text-white/60 text-sm">
                {t('footer.desc')}
              </p>
            </div>

            <div>
              <h4 className="text-white/80 text-sm uppercase tracking-wide mb-4 font-semibold">{t('nav.contact')}</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><a href="https://www.instagram.com/mysyshyn_target/" target="_blank" rel="noopener noreferrer" onClick={() => trackInstagramClick('footer')} className="hover:text-white transition">Instagram</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white/80 text-sm uppercase tracking-wide mb-4 font-semibold">{t('nav.sections')}</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><a href="#about" className="hover:text-white transition">{t('nav.about')}</a></li>
                <li><a href="#process" className="hover:text-white transition">{t('nav.adsSetup')}</a></li>
                <li><a href="#budget" className="hover:text-white transition">{t('nav.adBudget')}</a></li>
                <li><a href="#faq" className="hover:text-white transition">{t('nav.faq')}</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/5 pt-8">
            <p className="text-white/40 text-sm text-center">
              {t('footer.rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
